import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { contentApi, ContentType } from '@/services/contentApi';

interface LineagePanelProps {
  contentType: ContentType;
  contentId: string;
}

// Maps a content type to the page that shows its own detail view — used so
// lineage nodes are clickable. Only the types with a real standalone detail
// route are linked; the rest render as plain (non-clickable) labels.
const DETAIL_ROUTE: Partial<Record<ContentType, (id: string) => string>> = {
  news_article: (id) => `/app/newsroom/articles/${id}`,
  audio_to_video_job: (id) => `/app/create-video?jobId=${id}`,
};

const RELATION_LABEL: Record<string, string> = {
  translated_to: 'translated to',
  rendered_to: 'rendered to',
  clipped_to: 'clipped to',
  published_to: 'published to',
};

/**
 * Renders the connected-content chain for a piece of content — e.g. an
 * article that became a TTS job that became a video — as a simple linear
 * sequence of nodes. The underlying graph can branch, but in every real
 * pipeline today it's a straight chain, so a horizontal list reads more
 * clearly than a full node-and-edge diagram would for what's actually here.
 */
const LineagePanel: React.FC<LineagePanelProps> = ({ contentType, contentId }) => {
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['content-lineage', contentType, contentId],
    queryFn: () => contentApi.getLineage(contentType, contentId),
  });

  if (!data || data.nodes.length <= 1) {
    return null; // nothing connected — don't show an empty panel
  }

  // Order nodes by following edges from whichever node has no incoming edge
  // (the chain's start); falls back to the raw node order if the graph
  // isn't a simple chain (e.g. a future branching pipeline).
  const nodeByKey = new Map(data.nodes.map((n) => [n.key, n]));
  const nextByKey = new Map(data.edges.map((e) => [e.from, e]));
  const hasIncoming = new Set(data.edges.map((e) => e.to));
  const startNode = data.nodes.find((n) => !hasIncoming.has(n.key)) || data.nodes[0]!;

  const ordered = [startNode];
  let current = startNode;
  while (nextByKey.has(current.key)) {
    const edge = nextByKey.get(current.key)!;
    const next = nodeByKey.get(edge.to);
    if (!next || ordered.includes(next)) break;
    ordered.push(next);
    current = next;
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Connected content</h3>
      <div className="flex flex-wrap items-center gap-2">
        {ordered.map((node, i) => {
          const route = DETAIL_ROUTE[node.contentType]?.(node.contentId);
          const isCurrent = node.contentType === contentType && node.contentId === contentId;
          return (
            <React.Fragment key={node.key}>
              {i > 0 && <ArrowRightIcon className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" />}
              <button
                disabled={!route || isCurrent}
                onClick={() => route && navigate(route)}
                className={`text-xs px-2.5 py-1.5 rounded-md border ${
                  isCurrent
                    ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                    : route
                    ? 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    : 'border-gray-200 dark:border-gray-700 text-gray-400 cursor-default'
                }`}
              >
                {node.title}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default LineagePanel;
